<template>
  <div data-testid="tm-results-report-tab">
    <TMPendingPayoutsView
      v-if="pendingPayoutsActive"
      :collection="pendingPayoutsContext.collection"
      :report-date="pendingPayoutsContext.reportDate"
      :analysis-details="analysisDetails"
      @back="onPendingPayoutsBack"
    />

    <TMReportDrilldownView
      v-else-if="drilldownActive"
      ref="drilldownView"
      :field="drilldownActive.field"
      :summary-amount="drilldownActive.summaryAmount"
      :records="drilldownRecords"
      :table-headers="drilldownTableHeaders"
      :total-count="drilldownTotalCount"
      :page="drilldownPage"
      :page-count="drilldownPageCount"
      :loading="drilldownLoading"
      :active-filter-count="activeDrilldownFilterCount"
      :active-sort-count="activeDrilldownSortCount"
      :applied-filters="drilldownAppliedFilters"
      :applied-sort="drilldownAppliedSort"
      :default-filters="drilldownDefaultFilters"
      :default-sort="drilldownDefaultSort"
      :filter-columns="drilldownFilterColumns"
      :download-history="filteredDrilldownDownloadHistory"
      :loading-history="loadingHistory"
      :buckets="drilldownBuckets"
      :column-type-map="columnTypeMap"
      :error="drilldownError"
      :enable-saved-filter-sort="false"
      @back="onDrilldownBack"
      @refresh="fetchDrilldownData({ forceRefresh: true })"
      @page-change="onDrilldownPageChange"
      @apply-filters="onDrilldownApplyFilters"
      @apply-sort="onDrilldownApplySort"
      @generate-result="onDrilldownGenerateResult"
      @refresh-download-history="$emit('refresh-download-history')"
      @download-history-file="$emit('download-history-file', $event)"
    />

    <TMResultsReportV2
      v-show="!drilldownActive && !pendingPayoutsActive"
      ref="report"
      :collection="collection"
      :analysis-details="analysisDetails"
      :run-details="runDetails"
      :file-config-info="fileConfigInfo"
      :download-history="downloadHistory"
      :loading-history="loadingHistory"
      :tab-name="tabName"
      :result-types="resultTypes"
      @generate-result="$emit('generate-result', $event)"
      @refresh-download-history="$emit('refresh-download-history')"
      @download-history-file="$emit('download-history-file', $event)"
      @open-drilldown="onOpenDrilldown"
      @open-pending-payouts="onOpenPendingPayouts"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import TMResultsReportV2 from './TMResultsReportV2.vue';
import TMReportDrilldownView from './TMReportDrilldownView.vue';
import TMPendingPayoutsView from './TMPendingPayoutsView.vue';
import { buildColumnTypeMap } from '../shared/tmResultsDataTableUtils.js';
import {
  buildTableHeadersFromApi,
  buildFilterColumnsFromApi,
  countActiveFilterConditions,
  normalizeSortItems,
  normalizeDefaultFilters,
} from './tmReportDrilldownViewUtils.js';
import { DEFAULT_PAGE_LIMIT } from '../shared/tmResultsDataTableUtils.js';
import { TM_DOWNLOAD_SECTIONS } from '../../../../utils/constants.js';

export default {
  name: 'TMResultsReportTab',

  components: {
    TMResultsReportV2,
    TMReportDrilldownView,
    TMPendingPayoutsView,
  },

  props: {
    collection: {
      type: String,
      required: true,
    },
    analysisDetails: {
      type: Object,
      required: true,
    },
    runDetails: {
      type: Object,
      required: true,
    },
    fileConfigInfo: {
      type: Object,
      default: () => ({}),
    },
    downloadHistory: {
      type: Array,
      default: () => [],
    },
    loadingHistory: {
      type: Boolean,
      default: false,
    },
    tabName: {
      type: String,
      default: '',
    },
    resultTypes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      drilldownRecords: [],
      drilldownApiHeaders: [],
      drilldownTableHeaders: [],
      drilldownFilterColumns: [],
      drilldownTotalCount: 0,
      drilldownPage: 1,
      drilldownPageLimit: DEFAULT_PAGE_LIMIT,
      drilldownPageCount: 0,
      drilldownLoading: false,
      drilldownBuckets: null,
      drilldownAppliedFilters: { groups: [], group_connectors: [] },
      drilldownAppliedSort: [],
      drilldownDefaultFilters: { groups: [], group_connectors: [] },
      drilldownDefaultSort: [],
      drilldownInitialLoadComplete: false,
      drilldownExportFilters: null,
      drilldownError: null,
      pendingPayoutsContext: null,
    };
  },

  computed: {
    ...mapGetters('transactionMatching', ['reportDrilldownActive']),

    drilldownActive() {
      return this.reportDrilldownActive;
    },

    pendingPayoutsActive() {
      return !!this.pendingPayoutsContext;
    },

    columnTypeMap() {
      return buildColumnTypeMap(this.fileConfigInfo);
    },

    activeDrilldownFilterCount() {
      return countActiveFilterConditions(this.drilldownAppliedFilters);
    },

    activeDrilldownSortCount() {
      return this.drilldownAppliedSort.length;
    },

    filteredDrilldownDownloadHistory() {
      const field = this.drilldownActive?.field;
      return (this.downloadHistory || []).filter((file) => {
        if (file.section !== TM_DOWNLOAD_SECTIONS.TM_REPORT_DRILLDOWN) return false;
        if (!field || !file.field) return true;
        return file.field === field;
      });
    },
  },

  watch: {
    drilldownActive: {
      async handler(active, previous) {
        if (active && (!previous || previous.field !== active.field)) {
          this.resetDrilldownFetchState();
          await this.fetchDrilldownData();
        }
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    this.$store.commit('transactionMatching/CLEAR_REPORT_DRILLDOWN');
  },

  async mounted() {
    try {
      await this.fetchFormData();
    } catch (err) {
      console.error('[TMResultsReportTab]: prefetch formData error', err);
    }
  },

  methods: {
    ...mapActions('transactionMatching', ['fetchReportDrilldown', 'fetchFormData']),

    resetDrilldownFetchState() {
      this.drilldownRecords = [];
      this.drilldownApiHeaders = [];
      this.drilldownTableHeaders = [];
      this.drilldownFilterColumns = [];
      this.drilldownTotalCount = 0;
      this.drilldownPage = 1;
      this.drilldownPageCount = 0;
      this.drilldownBuckets = null;
      this.drilldownAppliedFilters = { groups: [], group_connectors: [] };
      this.drilldownAppliedSort = [];
      this.drilldownDefaultFilters = { groups: [], group_connectors: [] };
      this.drilldownDefaultSort = [];
      this.drilldownInitialLoadComplete = false;
      this.drilldownExportFilters = null;
      this.drilldownError = null;
    },

    buildDrilldownTableHeaders() {
      this.drilldownTableHeaders = buildTableHeadersFromApi(this.drilldownApiHeaders, this.columnTypeMap);
      this.drilldownFilterColumns = buildFilterColumnsFromApi(this.drilldownApiHeaders);
    },

    buildDrilldownRequestPayload({ forceRefresh = false } = {}) {
      const active = this.drilldownActive;
      if (!active) return null;

      const sort = this.drilldownAppliedSort.map((item) => ({
        field: item.key,
        direction: item.direction,
      }));

      const payload = {
        client: this.analysisDetails.client,
        version: 'v2',
        runId: this.runDetails._id,
        reportDate: active.reportDate,
        field: active.field,
        filters: this.drilldownAppliedFilters,
        pagination: {
          page: this.drilldownPage,
          limit: this.drilldownPageLimit,
        },
      };

      if (active.reportTable) {
        payload.reportTable = active.reportTable;
      }
      if (sort.length > 0) {
        payload.sort = sort;
      }
      if (forceRefresh) {
        payload.forceRefresh = true;
      }

      return payload;
    },

    applyDrilldownResponse(data = {}) {
      this.drilldownError = null;
      this.drilldownRecords = data.records || [];
      this.drilldownTotalCount = data.totalCount || 0;
      this.drilldownPage = data.page || this.drilldownPage;
      this.drilldownPageCount = data.totalPages || Math.ceil(this.drilldownTotalCount / this.drilldownPageLimit) || 0;
      this.drilldownApiHeaders = data.headers || [];
      this.drilldownBuckets = Array.isArray(data.buckets) && data.buckets.length ? data.buckets : null;
      this.drilldownExportFilters = data.filters || null;

      this.drilldownDefaultFilters = normalizeDefaultFilters(data.defaultFilters);
      this.drilldownDefaultSort = normalizeSortItems(data.defaultSort);

      if (data.appliedFilters) {
        this.drilldownAppliedFilters = normalizeDefaultFilters(data.appliedFilters);
      }
      if (data.appliedSort) {
        this.drilldownAppliedSort = normalizeSortItems(data.appliedSort);
      }

      if (!this.drilldownInitialLoadComplete) {
        this.drilldownInitialLoadComplete = true;
        let needsReload = false;

        if (this.drilldownDefaultSort.length > 0 && this.drilldownAppliedSort.length === 0) {
          this.drilldownAppliedSort = JSON.parse(JSON.stringify(this.drilldownDefaultSort));
          needsReload = true;
        }

        const hasDefaultFilters = (this.drilldownDefaultFilters.groups || []).length > 0;
        if (hasDefaultFilters && (this.drilldownAppliedFilters.groups || []).length === 0) {
          this.drilldownAppliedFilters = JSON.parse(JSON.stringify(this.drilldownDefaultFilters));
          needsReload = true;
        }

        this.buildDrilldownTableHeaders();

        if (needsReload) {
          this.$nextTick(() => {
            this.fetchDrilldownData();
          });
          return;
        }
      }

      this.buildDrilldownTableHeaders();
    },

    async fetchDrilldownData({ forceRefresh = false } = {}) {
      const payload = this.buildDrilldownRequestPayload({ forceRefresh });
      if (!payload) return;

      if (forceRefresh) {
        this.drilldownRecords = [];
        this.drilldownBuckets = null;
        this.drilldownError = null;
      }

      this.drilldownLoading = true;
      try {
        const data = await this.fetchReportDrilldown(payload);
        this.applyDrilldownResponse(data || {});
      } catch (err) {
        console.error('[TMResultsReportTab]: fetchDrilldownData error', err);
        const status = err?.response?.status;
        const message = err?.response?.data?.message || err?.message || 'Failed to load report drill-down data';

        if (status === 404) {
          this.drilldownError = 'not_configured';
        } else {
          this.drilldownError = 'generic';
        }

        this.$store.commit('sbError', message, { root: true });
      } finally {
        this.drilldownLoading = false;
      }
    },

    async onOpenDrilldown(payload) {
      this.pendingPayoutsContext = null;
      this.$store.commit('transactionMatching/SAVE_REPORT_SNAPSHOT', {
        selectedDate: payload.selectedDate,
        reportRecord: payload.reportRecord,
        collapsedSections: payload.collapsedSections,
      });
      this.$store.commit('transactionMatching/SET_REPORT_DRILLDOWN_ACTIVE', {
        field: payload.field,
        tableName: payload.tableName,
        reportTable: payload.reportTable || null,
        summaryAmount: payload.summaryAmount,
        reportDate: payload.reportDate,
      });
    },

    onOpenPendingPayouts(payload) {
      this.$store.commit('transactionMatching/SET_REPORT_DRILLDOWN_ACTIVE', null);
      this.resetDrilldownFetchState();
      this.$store.commit('transactionMatching/SAVE_REPORT_SNAPSHOT', {
        selectedDate: payload.selectedDate,
        reportRecord: payload.reportRecord,
        collapsedSections: payload.collapsedSections,
      });
      this.pendingPayoutsContext = {
        collection: payload.collection,
        reportDate: payload.reportDate,
      };
    },

    onPendingPayoutsBack() {
      this.pendingPayoutsContext = null;

      this.$nextTick(() => {
        const snapshot = this.$store.getters['transactionMatching/reportSnapshot'];
        if (snapshot && this.$refs.report?.hydrateFromSnapshot) {
          this.$refs.report.hydrateFromSnapshot(snapshot);
        }
      });
    },

    onDrilldownBack() {
      this.$store.commit('transactionMatching/SET_REPORT_DRILLDOWN_ACTIVE', null);
      this.resetDrilldownFetchState();

      this.$nextTick(() => {
        const snapshot = this.$store.getters['transactionMatching/reportSnapshot'];
        if (snapshot && this.$refs.report?.hydrateFromSnapshot) {
          this.$refs.report.hydrateFromSnapshot(snapshot);
        }
      });
    },

    onDrilldownGenerateResult() {
      const { filters, sort } = this.$refs.drilldownView?.getFiltersAndSortForExport({
        apiExportFilters: this.drilldownExportFilters,
      }) || {};

      this.$emit('generate-result', {
        section: TM_DOWNLOAD_SECTIONS.TM_REPORT_DRILLDOWN,
        version: 'v2',
        field: this.drilldownActive?.field || null,
        reportTable: this.drilldownActive?.reportTable || null,
        runId: this.runDetails?._id || null,
        reportDate: this.drilldownActive?.reportDate || null,
        filters: filters || null,
        sort: sort || null,
      });
    },

    onDrilldownPageChange(page) {
      this.drilldownPage = page;
      this.fetchDrilldownData();
    },

    onDrilldownApplyFilters(filters) {
      this.drilldownAppliedFilters = filters || { groups: [], group_connectors: [] };
      this.drilldownPage = 1;
      this.fetchDrilldownData();
    },

    onDrilldownApplySort(sort) {
      this.drilldownAppliedSort = Array.isArray(sort) ? sort : [];
      this.drilldownPage = 1;
      this.fetchDrilldownData();
    },
  },
};
</script>
